import * as THREE from "three";
import { ColladaLoader, GLTFLoader } from "three-stdlib";
import { getSceneState } from "../stores/sceneStore";
import { logger } from "../utils/logger";
import {
  assertMeshFileWithinBrowserImportLimit,
  assertObjTextWithinBrowserImportLimit,
  showBrowserImportLimitIssue,
  userMessageForImportError,
} from "../utils/importLimits";

// Cache for blob URLs to avoid recreating them
const blobUrlCache = new Map();

// Cache for loaded meshes to avoid reloading them
const meshCache = new Map();

// Cache for file content to avoid re-downloading identical files
const fileContentCache = new Map();

// Concurrency limiter for mesh parsing to avoid main-thread thrash
const MAX_CONCURRENT_MESH_LOADS = 4;
let activeMeshLoads = 0;
const meshLoadQueue = [];
let queuedMeshLoadFailures = 0;
let queuedMeshLoadFailureToastId = null;
let colladaLoader = null;
let gltfLoader = null;

function getColladaLoader() {
  if (!colladaLoader) colladaLoader = new ColladaLoader();
  return colladaLoader;
}

function getGltfLoader() {
  if (!gltfLoader) gltfLoader = new GLTFLoader();
  return gltfLoader;
}

function undoColladaZUpSceneRotation(scene) {
  const zUpLoaderQuat = new THREE.Quaternion().setFromEuler(new THREE.Euler(-Math.PI / 2, 0, 0));
  if (scene?.quaternion?.angleTo?.(zUpLoaderQuat) < 1e-6) {
    scene.quaternion.identity();
    scene.updateMatrix?.();
    scene.updateMatrixWorld?.(true);
  }
}

function normalizeMeshDependencyPath(value) {
  return String(value || "")
    .trim()
    .replace(/\\/g, "/")
    .replace(/^\.\/+/, "")
    .replace(/^\/+/, "");
}

function getBlobURLForGltfDependency(url, fileMap) {
  if (!fileMap || typeof fileMap !== "object") return url;
  if (!url || /^(?:blob:|data:|https?:)/i.test(url)) return url;

  const normalizedUrl = normalizeMeshDependencyPath(url);
  const basename = normalizedUrl.split("/").pop()?.toLowerCase() || normalizedUrl.toLowerCase();
  const match = Object.keys(fileMap).find((key) => {
    const normalizedKey = normalizeMeshDependencyPath(key);
    if (normalizedKey.toLowerCase() === normalizedUrl.toLowerCase()) return true;
    return normalizedKey.split("/").pop()?.toLowerCase() === basename;
  });
  if (!match || !fileMap[match]) return url;

  const file = fileMap[match];
  const cacheKey = `gltf-dependency:${match}:${file.size || 0}:${file.lastModified || 0}`;
  if (blobUrlCache.has(cacheKey)) return blobUrlCache.get(cacheKey);

  const blobUrl = URL.createObjectURL(file);
  blobUrlCache.set(cacheKey, blobUrl);
  return blobUrl;
}

function createGltfLoaderForFileMap(fileMap) {
  if (!fileMap || typeof fileMap !== "object") return getGltfLoader();
  const manager = new THREE.LoadingManager();
  manager.setURLModifier((url) => getBlobURLForGltfDependency(url, fileMap));
  return new GLTFLoader(manager);
}

function recordMeshLoadFailure() {
  if (typeof window === "undefined") return;
  queuedMeshLoadFailures += 1;
  if (queuedMeshLoadFailureToastId != null) return;
  queuedMeshLoadFailureToastId = window.setTimeout(() => {
    const count = queuedMeshLoadFailures;
    queuedMeshLoadFailures = 0;
    queuedMeshLoadFailureToastId = null;
    if (count <= 0) return;
    try {
      window.dispatchEvent(
        new CustomEvent("critter-toast", {
          detail: { message: `${count} mesh(es) failed to load` },
        })
      );
    } catch {
      // Toasts are best-effort UI feedback.
    }
  }, 500);
}

function showMeshImportWarning(error) {
  const message = userMessageForImportError(error, "Mesh failed to load.");
  logger.warn("[meshLoader] Mesh import warning:", error);
  if (showBrowserImportLimitIssue(error)) return;
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent("critter-toast", { detail: { message } }));
  } catch {
    // Toasts are best-effort UI feedback.
  }
}

function scheduleMeshLoad(task) {
  const runTask = () => {
    activeMeshLoads += 1;
    try {
      task(() => {
        activeMeshLoads -= 1;
        if (meshLoadQueue.length > 0) {
          const next = meshLoadQueue.shift();
          // Run next on microtask to avoid deep recursion
          Promise.resolve().then(next);
        }
      });
    } catch (e) {
      activeMeshLoads -= 1;
      if (meshLoadQueue.length > 0) {
        const next = meshLoadQueue.shift();
        Promise.resolve().then(next);
      }
      // Surface error without breaking callers
      logger.error("[meshLoader] Uncaught mesh load task error", e);
      recordMeshLoadFailure();
    }
  };

  if (activeMeshLoads < MAX_CONCURRENT_MESH_LOADS) {
    runTask();
  } else {
    meshLoadQueue.push(runTask);
  }
}

// Utility to get a blob URL for a file
export function getBlobURL(filename, fileMap) {
  if (!filename) return null;

  // Check cache first
  const cacheKey = `${filename}_${fileMap ? Object.keys(fileMap).length : 0}`;
  if (blobUrlCache.has(cacheKey)) {
    return blobUrlCache.get(cacheKey);
  }

  // First try the exact filename
  let file = fileMap[filename];

  // Also try known preset robot folder prefixes
  const candidates = [
    filename,
    `assets/${filename}`,
    filename.startsWith("assets/") ? filename.replace("assets/", "") : null,
    `robots/boston_dynamics_spot/${filename}`,
    `public/robots/boston_dynamics_spot/${filename}`,
    `robots/ur5e/${filename}`,
    `public/robots/ur5e/${filename}`,
  ].filter(Boolean);

  for (const path of candidates) {
    if (!file && fileMap[path]) {
      file = fileMap[path];
      break;
    }
  }

  if (!file) {
    // console.warn(`[getBlobURL] File ${filename} not found in fileMap.`);
    const keys = Object.keys(fileMap || {});
    const hint = keys.find((k) => k.endsWith(`/${filename}`) || k.endsWith(filename));
    if (hint) {
      // console.warn(`[getBlobURL] Closest match in fileMap: ${hint}`);
    }
    return null;
  }

  assertMeshFileWithinBrowserImportLimit(file, filename);
  const url = URL.createObjectURL(file);

  // Cache the URL
  blobUrlCache.set(cacheKey, url);

  return url;
}

// Revoke session-scoped blob URLs when the current scene/file set is torn down.
export function clearBlobUrlCache() {
  blobUrlCache.forEach((url) => URL.revokeObjectURL(url));
  blobUrlCache.clear();
}

// Smart mesh cache clearing - preserve useful meshes
export function clearMeshCache() {
  // Don't clear mesh cache between robot loads - meshes are expensive to reload
  // Only clear if we're running out of memory
  if (meshCache.size > 500) {
    meshCache.clear();
  }
}

// Clear file content cache when needed
export function clearFileContentCache() {
  fileContentCache.clear();
}

// Get cached file content or download it
export async function getCachedFile(filePath, fileMap) {
  // Create a cache key based on file path and content hash
  const file = fileMap[filePath];
  if (!file) return null;

  // For now, just return the file directly
  return file;
}

// Load a mesh (OBJ or STL), apply scaling and material, and call callback with loaded mesh/group
export function loadMesh(
  meshName,
  fileMap,
  scale,
  material,
  loader,
  stlLoader,
  callback,
  isCollision = false
) {
  // Create cache key based on mesh name, scale, and fileMap signature
  const fileMapSignature = fileMap ? Object.keys(fileMap).sort().join(",") : "";
  // Cache should be independent of instance scale so geometry can be shared
  const cacheKey = `${meshName}_${fileMapSignature}`;

  // Check if mesh is already cached
  if (meshCache.has(cacheKey)) {
    const cachedMesh = meshCache.get(cacheKey);
    // Clone the cached mesh to avoid sharing references
    const clonedMesh = cachedMesh.clone();

    // Apply material and scale to the cloned mesh
    clonedMesh.traverse((child) => {
      if (child.isMesh) {
        child.material = material;
        if (!clonedMesh.userData?.preserveMeshHierarchy) {
          child.scale.set(...scale);
        }
      }
    });
    if (clonedMesh.userData?.preserveMeshHierarchy) {
      const hierarchyRoot = clonedMesh.children[0];
      if (hierarchyRoot) hierarchyRoot.scale.set(...scale);
    }

    callback(clonedMesh);
    return;
  }

  // Try resolving via mesh metadata first (e.g., mesh.url)
  let mappedPath = null;
  let fileType = null;
  try {
    const state = getSceneState();
    if (state && typeof meshName === "string") {
      const meshEntry = state.meshes?.[meshName];
      const candidate = meshEntry?.url;
      if (candidate && typeof candidate === "string" && candidate.length) {
        mappedPath = candidate;
        // Determine file type from URL
        const lower = candidate.toLowerCase();
        if (lower.endsWith(".stl")) {
          fileType = "stl";
        } else if (lower.endsWith(".dae")) {
          fileType = "dae";
        } else if (lower.endsWith(".glb") || lower.endsWith(".gltf")) {
          fileType = "gltf";
        } else if (lower.endsWith(".obj")) {
          fileType = "obj";
        }
      }
    }
  } catch (error) {
    logger.warn("[meshLoader] Failed to resolve mesh metadata from scene state", error);
  }

  let objUrl = null;
  try {
    objUrl = mappedPath ? getBlobURL(mappedPath, fileMap) : null;
  } catch (error) {
    showMeshImportWarning(error);
    recordMeshLoadFailure();
    callback(new THREE.Group());
    return;
  }

  if (!objUrl) {
    // Only show warnings for visual meshes, not collision meshes (which are often missing)
    if (!isCollision) {
      logger.warn(`[meshLoader] Mesh file for ${meshName} not found.`, {
        meshName,
        mappedPath,
        fileMapKeys: Object.keys(fileMap || {}).slice(0, 10),
        meshEntry: (() => {
          try {
            const state = getSceneState();
            return state?.meshes?.[meshName] || null;
          } catch {
            return null;
          }
        })(),
      });
    }
    return;
  }

  if (!fileType) {
    // Determine file type from URL if not already set
    const urlLower = objUrl.toLowerCase();
    if (urlLower.includes(".stl")) {
      fileType = "stl";
    } else if (urlLower.includes(".dae")) {
      fileType = "dae";
    } else if (urlLower.includes(".glb") || urlLower.includes(".gltf")) {
      fileType = "gltf";
    } else if (urlLower.includes(".obj")) {
      fileType = "obj";
    } else {
      // Default to obj if we can't determine
      fileType = "obj";
    }
  }

  const addToGroup = (obj, options = {}) => {
    const preserveHierarchy = options.preserveHierarchy === true;
    // Filter out LineSegments and other non-mesh objects, keeping only actual meshes
    const filteredObj = new THREE.Group();

    // Handle case where the entire object might be a LineSegments or other non-mesh type
    if (obj.type === "LineSegments" || obj.type === "Points" || obj.type === "Line") {
      logger.warn(`[meshLoader] ${meshName} is a ${obj.type}, skipping - expected mesh`);
      callback(new THREE.Group()); // Return empty group
      return;
    }

    // Handle case where obj is already a Mesh (e.g., from STL loader)
    if (obj.isMesh) {
      // This is a single mesh - add it directly
      obj.material = material;
      obj.name = meshName;
      obj.scale.set(...scale);
      filteredObj.add(obj);
      callback(filteredObj);
      return;
    }

    // Safety check: ensure obj is a valid object with traverse method
    if (!obj || typeof obj.traverse !== "function") {
      logger.warn(`[meshLoader] ${meshName} is not a valid object, skipping`);
      callback(new THREE.Group()); // Return empty group
      return;
    }

    if (preserveHierarchy) {
      let meshCount = 0;
      obj.traverse((child) => {
        if (child.isMesh) {
          meshCount += 1;
          child.material = material;
          child.name = meshName;
        }
      });
      if (meshCount === 0) {
        logger.warn(`[meshLoader] No valid meshes found in ${meshName} after loading glTF`);
        callback(new THREE.Group());
        return;
      }
      obj.name = meshName;
      obj.scale.set(...scale);
      filteredObj.add(obj);
      filteredObj.userData.preserveMeshHierarchy = true;

      const meshForCache = filteredObj.clone();
      meshForCache.traverse((child) => {
        if (child.isMesh) {
          if (child.geometry && child.geometry.clone) {
            child.geometry = child.geometry.clone();
          }
          child.material = null;
        }
      });
      const hierarchyRoot = meshForCache.children[0];
      if (hierarchyRoot) hierarchyRoot.scale.set(1, 1, 1);
      meshForCache.scale.set(1, 1, 1);
      meshForCache.userData.preserveMeshHierarchy = true;
      meshCache.set(cacheKey, meshForCache);

      callback(filteredObj);
      return;
    }

    // Recursively traverse and collect all valid meshes, filtering out LineSegments
    const collectMeshes = (object) => {
      object.traverse((child) => {
        if (child.isMesh) {
          // This is a proper mesh - keep it and apply material/scale
          child.material = material;
          child.name = meshName;
          child.scale.set(...scale);
          // Keep original mesh normals; do not recompute here
          filteredObj.add(child);
        } else if (
          child.type === "LineSegments" ||
          child.type === "Points" ||
          child.type === "Line"
        ) {
          // Skip LineSegments and other problematic objects - these are often artifacts from OBJ files
          logger.debug(`[meshLoader] Filtering out ${child.type} from ${meshName}`);
        }
        // Continue traversing for Groups and other container objects
      });
    };

    collectMeshes(obj);

    // Only proceed if we have actual meshes
    if (filteredObj.children.length === 0) {
      logger.warn(
        `[meshLoader] No valid meshes found in ${meshName} after filtering, skipping. Object type: ${obj.type}, children:`,
        obj.children
      );
      callback(new THREE.Group()); // Return empty group
      return;
    }

    // console.log(`[meshLoader] Successfully loaded ${filteredObj.children.length} mesh(es) for ${meshName}`);

    // Cache the mesh before applying material (so we can reuse the geometry)
    const meshForCache = filteredObj.clone();
    meshForCache.traverse((child) => {
      if (child.isMesh) {
        // Reset material and scale for caching; clone geometry to isolate from scene disposal
        if (child.geometry && child.geometry.clone) {
          child.geometry = child.geometry.clone();
        }
        child.material = null;
        child.scale.set(1, 1, 1);
      }
    });
    meshCache.set(cacheKey, meshForCache);

    callback(filteredObj);
  };

  // Use more aggressive loading with shorter timeouts
  if (fileType === "obj") {
    scheduleMeshLoad((done) => {
      const timeoutId = setTimeout(() => {
        logger.warn(`OBJ load timeout for ${meshName}, retrying...`);
        // Retry once on timeout (reschedules via queue)
        setTimeout(
          () =>
            loadMesh(meshName, fileMap, scale, material, loader, stlLoader, callback, isCollision),
          100
        );
        clearTimeout(timeoutId);
        done();
      }, 8000);

      // Sanitize OBJ text to remove line primitives before parsing
      fetch(objUrl)
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.text();
        })
        .then((text) => {
          clearTimeout(timeoutId);
          try {
            assertObjTextWithinBrowserImportLimit(text, meshName);
            // Remove lines starting with 'l ' (OBJ line elements) that create LineSegments
            const sanitized = text.replace(/^\s*l\s+.*$/gim, "");
            const obj = loader.parse(sanitized);
            addToGroup(obj);
          } catch (e) {
            logger.error("[meshLoader] Failed to parse sanitized OBJ", e);
            showMeshImportWarning(e);
          } finally {
            done();
          }
        })
        .catch((err) => {
          clearTimeout(timeoutId);
          logger.error("[meshLoader] Failed to fetch OBJ", err);
          done();
        });
    });
  } else if (fileType === "dae") {
    scheduleMeshLoad((done) => {
      const timeoutId = setTimeout(() => {
        logger.warn(`DAE load timeout for ${meshName}, retrying...`);
        setTimeout(
          () =>
            loadMesh(meshName, fileMap, scale, material, loader, stlLoader, callback, isCollision),
          100
        );
        clearTimeout(timeoutId);
        done();
      }, 8000);

      getColladaLoader().load(
        objUrl,
        (collada) => {
          clearTimeout(timeoutId);
          try {
            undoColladaZUpSceneRotation(collada.scene);
            addToGroup(collada.scene);
          } catch (e) {
            logger.error("[meshLoader] Error processing DAE scene", e);
            callback(new THREE.Group());
          } finally {
            done();
          }
        },
        undefined,
        (err) => {
          clearTimeout(timeoutId);
          logger.error("[meshLoader] Failed to load DAE", err);
          done();
        }
      );
    });
  } else if (fileType === "gltf") {
    scheduleMeshLoad((done) => {
      const timeoutId = setTimeout(() => {
        logger.warn(`glTF load timeout for ${meshName}, retrying...`);
        setTimeout(
          () =>
            loadMesh(meshName, fileMap, scale, material, loader, stlLoader, callback, isCollision),
          100
        );
        clearTimeout(timeoutId);
        done();
      }, 8000);

      createGltfLoaderForFileMap(fileMap).load(
        objUrl,
        (gltf) => {
          clearTimeout(timeoutId);
          try {
            addToGroup(gltf.scene, { preserveHierarchy: true });
          } catch (e) {
            logger.error("[meshLoader] Error processing glTF scene", e);
            callback(new THREE.Group());
          } finally {
            done();
          }
        },
        undefined,
        (err) => {
          clearTimeout(timeoutId);
          logger.error("[meshLoader] Failed to load glTF", err);
          done();
        }
      );
    });
  } else if (fileType === "stl") {
    scheduleMeshLoad((done) => {
      const timeoutId = setTimeout(() => {
        logger.warn(`STL load timeout for ${meshName}, retrying...`);
        setTimeout(
          () =>
            loadMesh(meshName, fileMap, scale, material, loader, stlLoader, callback, isCollision),
          100
        );
        clearTimeout(timeoutId);
        done();
      }, 8000);

      stlLoader.load(
        objUrl,
        (geometry) => {
          clearTimeout(timeoutId);
          try {
            // Validate geometry for NaN values
            if (!geometry || !geometry.attributes || !geometry.attributes.position) {
              logger.error("[meshLoader] Invalid STL geometry: missing attributes");
              callback(new THREE.Group());
              done();
              return;
            }

            const positions = geometry.attributes.position;
            if (!positions || !positions.array) {
              logger.error("[meshLoader] Invalid STL geometry: missing position array");
              callback(new THREE.Group());
              done();
              return;
            }

            // Check for NaN values in positions
            const positionArray = positions.array;
            let hasNaN = false;
            for (let i = 0; i < positionArray.length; i++) {
              if (isNaN(positionArray[i]) || !isFinite(positionArray[i])) {
                hasNaN = true;
                logger.error(
                  `[meshLoader] NaN or infinite value found in STL geometry at index ${i}: ${positionArray[i]}`
                );
                break;
              }
            }

            if (hasNaN) {
              logger.error(
                "[meshLoader] STL geometry contains NaN or infinite values. This may indicate a corrupted or invalid STL file."
              );
              callback(new THREE.Group());
              done();
              return;
            }

            // Check if geometry has valid vertex count
            if (positions.count === 0) {
              logger.warn(`[meshLoader] STL geometry for ${meshName} has no vertices`);
              callback(new THREE.Group());
              done();
              return;
            }

            // Compute bounding sphere to validate geometry
            try {
              geometry.computeBoundingSphere();
              if (!geometry.boundingSphere || !isFinite(geometry.boundingSphere.radius)) {
                logger.error("[meshLoader] Invalid bounding sphere for STL geometry");
                callback(new THREE.Group());
                done();
                return;
              }
            } catch (e) {
              logger.error("[meshLoader] Failed to compute bounding sphere for STL geometry", e);
              callback(new THREE.Group());
              done();
              return;
            }

            // Validate scale values
            if (scale.some((s) => isNaN(s) || !isFinite(s) || s === 0)) {
              logger.error("[meshLoader] Invalid scale values for STL geometry", scale);
              callback(new THREE.Group());
              done();
              return;
            }

            const mesh = new THREE.Mesh(geometry, material);
            mesh.scale.set(...scale);

            // Cache the geometry: use a cloned geometry to isolate from scene disposal
            const meshForCache = new THREE.Mesh(geometry.clone());
            meshForCache.traverse((child) => {
              if (child.isMesh) {
                child.material = null;
                child.scale.set(1, 1, 1);
              }
            });
            meshCache.set(cacheKey, meshForCache);

            addToGroup(mesh);
          } catch (e) {
            logger.error("[meshLoader] Error processing STL geometry", e);
            callback(new THREE.Group());
          } finally {
            done();
          }
        },
        undefined,
        (err) => {
          clearTimeout(timeoutId);
          logger.error("[meshLoader] Failed to load STL", err);
          done();
        }
      );
    });
  }
}
